import React, { useState } from "react";
import * as auth from "@utils/auth";
import Box from "@mui/material/Box";
import Overlay from "@containers/Overlay";
import Grid from "@mui/material/Grid";
import SendIcon from "@mui/icons-material/Send";
import DashboardHeader from "@components/DashboardHeader";
import Camera from "@components/Camera";
import { LANGUAGE } from "@/constants";
import { useGlobalContext, useGlobalContextUpdate } from "@contexts/Global";
import Traductor from "@utils/traductor";
import UploadFile from "@components/UploadFile";
import Preview from "@components/Preview";
import Button from "@mui/material/Button";
import { DataSource } from "@factory/DataSource";
import { getToDayDate } from "@/utils/date";
import { isIOS } from "@/utils/browser";
import { checkCurrentTable } from "@/utils/tables";

const traductor = new Traductor(LANGUAGE);
const api = DataSource(auth.getEnv());

const Options = (props = {}) => {
  return (
    <Grid
      container
      justifyContent="flex-center"
      alignItems="center"
      spacing={2}
      sx={{
        marginBottom: "0px",
        marginTop: "20px",
        zIndex: 1,
        position: "relative",
        top: "-30px",
        visibility: props.hide ? "hidden" : "visible",
        height: props.hide ? 0 : "auto",
      }}
    >
      <Grid item md={6} sm={6} xs={6}>
        <UploadFile
          color={props.color}
          onPreview={props.onPreview}
          labels={{ uploadFile: traductor.translate("uploadFile") }}
        />
      </Grid>

      <Grid item md={6} sm={6} xs={6}>
        <Button
          onClick={props.onSave}
          variant="contained"
          disabled={!props.enableSave}
          sx={{
            width: "100%",
            backgroundColor: props.color,
            borderRadius: 0,
            height: "50px",
            fontSize: "12px",
          }}
          endIcon={<SendIcon />}
        >
          {props.labels.save}
        </Button>
      </Grid>
    </Grid>
  );
};

export const DocumentModule = (props = {}) => {
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [inputRef, setInputRef] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [showPreviewOptions, setShowPreviewOptions] = useState(true);
  const globalContext = useGlobalContext();
  const setGlobalContext = useGlobalContextUpdate();
  const showPreview = previewUrl !== "" && filePreview !== null;
  const { modalColor: color, data = {}, corporation = "" } = globalContext;
  const { option = "" } = props;
  const { currentTable = {}, placeId = "", id = "", tables } = data;

  const handlePreview = ({ url = "", file = {}, input }) => {
    setPreviewUrl(url);
    setFilePreview(file);
    setInputRef(input);
    setShowPreviewOptions(true);
  };

  const handleInvalidFile = () => {
    setGlobalContext({
      notificationType: "error",
      notificationMessage: traductor.translate("invalidFileType"),
    });
  };

  const handleTakePicture = (file) => {
    setFile(file);
  };

  const handleDismissPreview = () => {
    setShowPreviewOptions(false);
    setFilePreview(null);
    setPreviewUrl("");
    if (inputRef) {
      inputRef.value = "";
    }
  };

  const handleConfirmFile = () => {
    setFile(filePreview);
    setShowPreviewOptions(false);
  };

  const handleSaveFileFailed = () => {
    setGlobalContext({
      notificationType: "error",
      notificationMessage: traductor.translate("fileUploadFailed"),
      showNotification: true,
      showOverlay: false,
    });
    setShowPreviewOptions(false);
    setFilePreview(null);
    setFile(null);
    setPreviewUrl("");
    if (inputRef) {
      inputRef.value = "";
    }
  };

  const handleSaveFileSuccess = async () => {
    const updatedTable = checkCurrentTable({
      option,
      currentTable,
      corporation,
    });
    const isTableReported =
      currentTable.E14_sen &&
      currentTable.cuenta_votos_sen &&
      currentTable.ingreso_datos_sen &&
      currentTable.E14_consulta &&
      currentTable.cuenta_votos_consulta &&
      currentTable.ingreso_datos_consulta &&
      currentTable.E14_cam &&
      currentTable.cuenta_votos_cam &&
      currentTable.ingreso_datos_cam;

    if (isTableReported) {
      const { result = null } = await api.reportTable({
        placeId,
        mesa: currentTable.mesa,
      });
      if (result && result.success === false) {
        return setGlobalContext({
          notificationType: "error",
          notificationMessage: traductor.translate("fileUploadFailed"),
          showNotification: true,
        });
      }
    }
    const { ok = false, result } = await api.updateUserTable(id, {
      check_mesas: tables,
    });
    if (ok && result.success) {
      return setGlobalContext({
        notificationType: "success",
        notificationMessage: traductor.translate("fileUploadSuccess"),
        showNotification: true,
        showOverlay: false,
        isModalOpen: false,
        data: {
          ...data,
          currentTable: updatedTable,
        }
      });
    }
    setGlobalContext({
      notificationType: "error",
      notificationMessage: traductor.translate("fileUploadFailed"),
      showNotification: true,
    });
  };

  const buildUploadFilePayload = (file) => {
    const { table, place = "" } = data;
    const { option = "" } = props;
    const path = `/${place.replace(
      "-",
      "/"
    )}/${corporation}/${option}/${getToDayDate()}/Mesa ${table}`;
    return { file, table, path };
  };

  const handleSaveFile = async () => {
    setGlobalContext({ showOverlay: true });
    try {
      const { ok = true, error = null } = await api.uploadFile(
        buildUploadFilePayload(file)
      );
      if (ok) {
        return handleSaveFileSuccess();
      }
      console.error(error);
      handleSaveFileFailed(error);
    } finally {
      // request done
    }
  };

  const renderCamera = () => {
    if (isIOS()) return null;
    return (
      <Camera
        color={color}
        openCamera={true}
        onTakepicture={handleTakePicture}
        labels={{
          takePicture: traductor.translate("takePicture"),
          repeatPhoto: traductor.translate("repeatPhoto"),
        }}
      />
    );
  };

  return (
    <Box>
      <DashboardHeader
        name={data.name}
        place={data.place}
        table={data.table}
        corporation={corporation}
        corporationFixed={true}
      />

      <Options
        color={color}
        labels={{ save: traductor.translate("save") }}
        enableSave={file !== null}
        onPreview={handlePreview}
        onSave={handleSaveFile}
        onInvalidFile={handleInvalidFile}
        hide={showPreviewOptions && showPreview}
      />
      {showPreview ? (
        <Preview
          url={previewUrl}
          showOptions={showPreviewOptions}
          onConfirm={handleConfirmFile}
          onDismiss={handleDismissPreview}
          labels={{
            confirm: traductor.translate("confirm"),
            dismiss: traductor.translate("dismiss"),
          }}
        />
      ) : (
        renderCamera()
      )}
      <Overlay />
    </Box>
  );
};
